import {
  type DesignerSourceFile,
  type SandboxSession,
} from '@packages/domains/code-libraries/data-types';
import type {
  FunctionsPreviewConfig,
  FunctionsPreviewCredentials,
  FunctionsReadiness,
} from '@packages/systems/code-modules/constants/functions';
import {type AIAssistantStoreState} from '@packages/systems/ai/assistant-next/frontend';
import {
  isLoopbackBundleUrl,
  parseCodeModuleId,
  type CodeModuleId,
} from '@packages/systems/code-modules/core';
import {
  selectComponentCanvasCodeComponentSource,
  selectComponentCanvasComponentId,
} from '@packages/systems/component-canvas/designer';
import type {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {type PageState} from '@packages/systems/core/records/PageRecord';
import {SiteDataStateType} from '@packages/systems/designer/site-data';
import {createUseDispatch, createUseSelector} from '@packages/systems/flux';
import {componentInstanceCountsSummary} from '@packages/systems/user-components/utils';
import {Component, Expressions} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {isCodeIsland} from '@packages/utilities/core-elements';
import {generateGuid} from '@packages/utilities/guid';
import {weakMemo} from '@packages/utilities/memo';
import {getLocalCodeComponents} from '@packages/utilities/platform';
import {selectComponents} from '@packages/utilities/platform-selectors';
import * as React from 'react';
import {createSelector} from 'reselect';
import {type CodeComponentUIState} from './CodeComponentUIState';
import {type RemoteSandboxState} from './RemoteSandboxStore';
import {getFunctionsPreview} from './FunctionsSandboxStore';
import {type FunctionsSandboxState} from './types';
import {
  codeComponentCreated,
  aiAssistantOpened,
  codeComponentSourceFileSelected,
  codeComponentUpdated,
  vccSourceFileContentSaved,
  type CodeLibrariesActions,
  type SandboxFileSystem,
  type VCCStyleOverridesMap,
} from './actionCreators';
import {
  INITIAL_CODE_COMPONENT_SOURCE,
  getUneditedCodeComponentTemplate,
  type CodeComponentTemplateBranding,
  type CodeComponentTemplateId,
  type SourceFilesState,
} from '@packages/systems/code-libraries/core';
import {SANDBOX_REQUIRED_TOOL_IDS, isSessionExpired} from './sandboxClient';
import {getCodeComponentTemplateBranding} from '../getCodeComponentTemplateBranding';

type CodeLibrariesStateSlice = {
  CodeComponentUIStore: CodeComponentUIState;
  DesignerStore: DesignerStateType;
  FunctionsSandboxStore: FunctionsSandboxState;
  PageStore: PageState;
  RemoteSandboxStore: RemoteSandboxState;
  SiteDataStore: SiteDataStateType;
  SourceFilesStore: SourceFilesState;
  AIAssistantStore: AIAssistantStoreState;
};

const selectSiteId = (state: CodeLibrariesStateSlice) =>
  state.SiteDataStore.siteId;

export type SiteCodeComponentLibrary = Omit<
  CodeComponentUIState,
  | 'localDevServerReachable'
  | 'localDevDiscoveredDescriptor'
  | 'localOverrideOptedOutSlugs'
  | 'sourceFileDrafts'
  | 'hasUnsavedCodeChanges'
  | 'draftsClearedVersion'
  | 'refusedSourceFileKeys'
  | 'sourceFileSaveRefusalVersion'
  | 'editorPane'
  | 'activeSourceFileKey'
  | 'isSourceTreeCollapsed'
  | 'isCreatorDismissed'
  | 'pendingCreatorPrompt'
  | 'pendingTemplateWelcomeComponentId'
  | 'pendingTemplateDrag'
  | 'creatorTemplateBranding'
  | 'isAwaitingClarification'
  | 'clarificationUserPrompt'
  | 'clarificationCreateTurnArmed'
  | 'vccStyleOverrides'
  | 'clearVccStyleOverridesOnNextEval'
  | 'skipVccStyleOverridesClearOnNextTurnEnd'
  | 'sentVccStyleOverrides'
  | 'vccApplySourceSnapshot'
  | 'vccApplySourceSnapshotClaimed'
  | 'instantAppliedVccStyleOverrides'
  | 'instantApplyPendingKeys'
  | 'vccTextOverrides'
  | 'sentVccTextOverrides'
  | 'clearVccTextOverridesOnNextEval'
  | 'skipVccTextOverridesClearOnNextTurnEnd'
  | 'vccDraftsWithheldFromTurn'
  | 'vccApplyCancelRequests'
  | 'vccTextEditing'
  | 'vccInstantApplyHighlight'
  | 'vccInstantApplyReport'
  | 'vccSelectedSourceRange'
  | 'pendingVisualEditingOnboarding'
  | 'onboardingAiTurnComplete'
  | 'onboardingCodeEvaluated'
  | 'onboardingAiWritePending'
  | 'onboardingAiBuildStartVersion'
  | 'showVisualEditingOnboarding'
  | 'visualEditingOnboardingStep'
  | 'hasMadeVisualEdit'
  | 'sourceFilesHydrationEpoch'
  | 'componentMapHydrationEpoch'
>;

export const useCodeComponentSelector =
  createUseSelector<CodeLibrariesStateSlice>();

export const useCodeComponentDispatch =
  createUseDispatch<CodeLibrariesActions>();

export function useCodeComponentCreated() {
  const dispatch = useCodeComponentDispatch();
  return React.useCallback(
    ({
      componentId = generateGuid(),
      templateId,
      // Read from the page even for blank stubs: the creator applies templates
      // later from the component canvas, which has no page styles to read.
      templateBranding = getCodeComponentTemplateBranding(),
    }: {
      componentId?: string;
      templateId?: CodeComponentTemplateId;
      templateBranding?: CodeComponentTemplateBranding;
    } = {}) => {
      dispatch(
        codeComponentCreated({
          componentId,
          entryKind: 'blank',
          templateBranding,
          ...(templateId ? {templateId} : {}),
        })
      );
      if (templateId) {
        dispatch(
          aiAssistantOpened({
            clearConversation: true,
            clearCmsCollections: true,
          })
        );
      }
    },
    [dispatch]
  );
}

export function useCodeComponentUpdated() {
  const dispatch = useCodeComponentDispatch();
  return React.useCallback(
    (payload: {componentId: string; code: string}) =>
      dispatch(codeComponentUpdated(payload)),
    [dispatch]
  );
}

export function useCodeComponentSourceFileSelected() {
  const dispatch = useCodeComponentDispatch();
  return React.useCallback(
    (fileKey: string | null) =>
      dispatch(codeComponentSourceFileSelected(fileKey)),
    [dispatch]
  );
}

export function useSourceFileContentSaved() {
  const dispatch = useCodeComponentDispatch();
  return React.useCallback(
    (file: DesignerSourceFile) => dispatch(vccSourceFileContentSaved(file)),
    [dispatch]
  );
}

export function isEditableCodeComponent(
  component: Component.ComponentType | null
): boolean {
  return component ? Boolean(Component.getSource(component)) : false;
}

// Fields rather than the store as inputs: this reaches CanvasContainer's
// mapped state, compared with shallowEqual, so a draft keystroke taking the
// whole store would re-render the canvas.
export const selectSiteCodeComponentLibrary = createSelector(
  (state: CodeLibrariesStateSlice) => state.CodeComponentUIStore !== undefined,
  (state: CodeLibrariesStateSlice) => state.CodeComponentUIStore?.js,
  (state: CodeLibrariesStateSlice) => state.CodeComponentUIStore?.css,
  (state: CodeLibrariesStateSlice) => state.CodeComponentUIStore?.buildVersion,
  (state: CodeLibrariesStateSlice) =>
    state.CodeComponentUIStore?.evaluatedVersion,
  (state: CodeLibrariesStateSlice) => state.CodeComponentUIStore?.diagnostics,
  (state: CodeLibrariesStateSlice) =>
    state.CodeComponentUIStore?.sourceRevisions,
  (
    hasStore,
    js,
    css,
    buildVersion,
    evaluatedVersion,
    diagnostics,
    sourceRevisions
  ): SiteCodeComponentLibrary | null => {
    if (!hasStore) return null;
    return {
      js: js || '',
      css: css || '',
      buildVersion: buildVersion || 0,
      evaluatedVersion: evaluatedVersion || 0,
      diagnostics: diagnostics || '',
      sourceRevisions,
    };
  }
);

const selectIsComponentCanvasActive = (state: CodeLibrariesStateSlice) =>
  Boolean(
    state.PageStore.get('componentPages').get(state.DesignerStore.currentPageId)
  );

/**
 * Returns true if the site is using any code components.
 * First, we check if there are any local copies (__SitePlugin components) that are code components.
 * If so, we then check if at least one of them is being used (has an instance count > 0).
 */
export const selectIsUsingCodeComponents = createSelector(
  selectIsComponentCanvasActive,
  (state: CodeLibrariesStateSlice) => state.DesignerStore.components,
  (state: CodeLibrariesStateSlice) =>
    state.DesignerStore.componentInstanceCounts,
  (isComponentCanvasActive, components, componentInstanceCounts) => {
    const codeComponents = getLocalCodeComponents(components);
    const names = Object.keys(codeComponents);
    if (names.length === 0) {
      return false;
    }

    const counts = componentInstanceCountsSummary(components)(
      isComponentCanvasActive
    )(componentInstanceCounts);

    return names.some((name) => Number(counts[name]) > 0);
  }
);
export function useIsUsingCodeComponents() {
  return useCodeComponentSelector(selectIsUsingCodeComponents);
}

export type SandboxReadiness =
  | {status: 'ready'; session: SandboxSession}
  | {status: 'no-components'}
  | {status: 'waiting'};

export const allSynced = (files: SandboxFileSystem): boolean =>
  Object.values(files).every((file) => file.status === 'SYNCED');

export const selectSandboxReadiness = createSelector(
  (state: CodeLibrariesStateSlice) => state.RemoteSandboxStore,
  (sandboxStore): SandboxReadiness => {
    if (Object.keys(sandboxStore.filesystem).length === 0) {
      return {status: 'no-components'};
    }

    if (
      sandboxStore.sandbox.status === 'CREATED' &&
      !isSessionExpired(sandboxStore.sandbox.value.session) &&
      allSynced(sandboxStore.filesystem)
    ) {
      return {status: 'ready', session: sandboxStore.sandbox.value.session};
    }

    return {status: 'waiting'};
  }
);

export const selectHasUnsavedCodeChanges = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.hasUnsavedCodeChanges;

export const selectCodeEditorPane = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.editorPane;

export const selectActiveSourceFileKey = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.activeSourceFileKey;

export const selectSourceFiles = (state: CodeLibrariesStateSlice) =>
  state.SourceFilesStore;

/** Bumps when a server load replaces the live component map wholesale. */
export const selectComponentMapHydrationEpoch = (
  state: CodeLibrariesStateSlice
) => state.CodeComponentUIStore.componentMapHydrationEpoch;

/**
 * Source files are site-global, but on a branch the multiplayer server never
 * syncs them (`SupportedFeatures.sourceFilesSupported` is main-partition
 * only), while the REST path has no partition check. A collection performed
 * on a branch would tombstone a site-global row while the still-connected
 * main site's client can resurrect it on its next whole-store push.
 */
export const selectIsPageBranch = (state: CodeLibrariesStateSlice) =>
  state.DesignerStore.isPageBranch;

export const selectIsSourceTreeCollapsed = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.isSourceTreeCollapsed;

export const selectSourceFileDrafts = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.sourceFileDrafts;

/** Keys whose save the write rules refused; the editor derives the reason. */
export const selectRefusedSourceFileKeys = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.refusedSourceFileKeys;

/** Bumps on every refused save, so the editor can react to the event. */
export const selectSourceFileSaveRefusalVersion = (
  state: CodeLibrariesStateSlice
) => state.CodeComponentUIStore.sourceFileSaveRefusalVersion;

/** Bumps when every draft is wiped at once -- see `draftsClearedVersion`. */
export const selectDraftsClearedVersion = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.draftsClearedVersion;

// The draft map changes on every keystroke; only membership reaches the tree.
// Collapsing to a signature keeps the set below reference-stable in between.
const selectSourceFileDraftKeySignature = createSelector(
  selectSourceFileDrafts,
  (drafts) => Object.keys(drafts).sort().join('\n')
);

/** Keys of every file with unsaved editor content, for the tree's dirty marks. */
export const selectDirtySourceFileKeys = createSelector(
  selectSourceFileDraftKeySignature,
  (signature): ReadonlySet<string> =>
    new Set(signature === '' ? [] : signature.split('\n'))
);

/**
 * Everything `saveCodeComponentDrafts` needs to write every draft out and
 * track `VCC Source File Saved` the same way the editor's own Save button
 * does. `componentId` is the canvas-focused component, not a per-draft id --
 * a main-source draft already names its own component in its key.
 */
export type CodeComponentDraftSaveInput = {
  drafts: Readonly<Record<string, string>>;
  files: SourceFilesState;
  componentId: string | undefined;
  siteId: string;
  workspaceId: string | null;
  userId: string;
};

export const selectCodeComponentDraftSaveInput = createSelector(
  selectSourceFileDrafts,
  selectSourceFiles,
  selectComponentCanvasComponentId,
  selectSiteId,
  (state: CodeLibrariesStateSlice) => state.SiteDataStore.workspaceId,
  (state: CodeLibrariesStateSlice) => state.SiteDataStore.userId,
  (
    drafts,
    files,
    componentId,
    siteId,
    workspaceId,
    userId
  ): CodeComponentDraftSaveInput => ({
    drafts,
    files,
    componentId,
    siteId,
    workspaceId,
    userId,
  })
);

/**
 * The source file the editor should display, or null to fall back to the
 * canvas-focused component's own source. Null when no file is selected or the
 * selected key no longer exists (e.g. the file was deleted or renamed).
 */
export const selectActiveSourceFile = createSelector(
  selectActiveSourceFileKey,
  selectSourceFiles,
  (key, files): DesignerSourceFile | null =>
    key && files[key] ? files[key] : null
);

export const selectIsCreatorDismissed = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.isCreatorDismissed;

export const selectPendingCreatorPrompt = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.pendingCreatorPrompt;

export const selectClarificationUserPrompt = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.clarificationUserPrompt;

export const selectIsAwaitingClarification = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.isAwaitingClarification;

export const selectShowCodeComponentClarification = (
  state: CodeLibrariesStateSlice
) => state.CodeComponentUIStore.isAwaitingClarification;

export const selectVCCStyleOverrides = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.vccStyleOverrides;

/**
 * The style drafts the AI Assistant still has to handle: `vccStyleOverrides` minus
 * whatever instant CSS apply (EXT-1219) already wrote or is about to. Those stay live
 * to keep the canvas right, but the assistant shouldn't redo them. Returns the map
 * itself when both sets are empty, so the reference stays stable in the steady state.
 */
export const selectVCCStyleOverridesAwaitingAssistant = createSelector(
  (state: CodeLibrariesStateSlice) =>
    state.CodeComponentUIStore.vccStyleOverrides,
  (state: CodeLibrariesStateSlice) =>
    state.CodeComponentUIStore.instantAppliedVccStyleOverrides,
  (state: CodeLibrariesStateSlice) =>
    state.CodeComponentUIStore.instantApplyPendingKeys,
  (overrides, instantApplied, pendingKeys): VCCStyleOverridesMap => {
    const appliedKeys = Object.keys(instantApplied);
    if (appliedKeys.length === 0 && pendingKeys.length === 0) return overrides;

    const excluded = new Set<string>(appliedKeys);
    for (const key of pendingKeys) excluded.add(key);

    const remaining: VCCStyleOverridesMap = {};
    for (const [key, entry] of Object.entries(overrides)) {
      if (excluded.has(key)) continue;
      remaining[key] = entry;
    }
    return remaining;
  }
);

export const selectCodeComponentEvaluatedVersion = (
  state: CodeLibrariesStateSlice
) => state.CodeComponentUIStore.evaluatedVersion;

export const selectCodeComponentBuildVersion = (
  state: CodeLibrariesStateSlice
) => state.CodeComponentUIStore.buildVersion;

export const selectIsVCCDraftsWithheldFromTurn = (
  state: CodeLibrariesStateSlice
) => state.CodeComponentUIStore.vccDraftsWithheldFromTurn;

export const selectIsVCCStyleOverridesClearPending = (
  state: CodeLibrariesStateSlice
) =>
  state.CodeComponentUIStore.clearVccStyleOverridesOnNextEval ||
  state.CodeComponentUIStore.clearVccTextOverridesOnNextEval;

export const selectVCCTextOverrides = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.vccTextOverrides;

/**
 * The source an apply started from, while that apply is still cancellable
 * (EXT-1976). Non-null exactly while Stop is on screen for the turn the apply
 * sent, so a cancel can put source back whichever writer changed it.
 */
export const selectVCCApplySourceSnapshot = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.vccApplySourceSnapshot;

/**
 * How many times the user has called off a pending draft apply (EXT-1976).
 * Watch it, don't read it: the value itself means nothing, a change means the
 * apply in flight was cancelled and whatever it already wrote should come back.
 */
export const selectVCCApplyCancelRequests = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.vccApplyCancelRequests;

export const selectVCCTextEditing = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.vccTextEditing;

export const selectVCCInstantApplyHighlight = (
  state: CodeLibrariesStateSlice
) => state.CodeComponentUIStore.vccInstantApplyHighlight;

export const selectVCCInstantApplyReport = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.vccInstantApplyReport;

export const selectVCCSelectedSourceRange = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.vccSelectedSourceRange;

export const selectShowVisualEditingOnboarding = (
  state: CodeLibrariesStateSlice
) => state.CodeComponentUIStore.showVisualEditingOnboarding;

export const selectVisualEditingOnboardingStep = (
  state: CodeLibrariesStateSlice
) => state.CodeComponentUIStore.visualEditingOnboardingStep;

export const selectHasMadeVisualEdit = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.hasMadeVisualEdit;

export const selectCodeLibrariesSiteShortName = (
  state: CodeLibrariesStateSlice
) => state.SiteDataStore.shortName;

export const selectCreatorTemplateBranding = (state: CodeLibrariesStateSlice) =>
  state.CodeComponentUIStore.creatorTemplateBranding;

export const selectShowCodeComponentCreator = createSelector(
  selectComponentCanvasCodeComponentSource,
  selectIsCreatorDismissed,
  (source, isCreatorDismissed) =>
    source?.code === INITIAL_CODE_COMPONENT_SOURCE && !isCreatorDismissed
);

export const selectCodeComponentTemplate = createSelector(
  selectComponentCanvasCodeComponentSource,
  (state: CodeLibrariesStateSlice) =>
    state.AIAssistantStore.codeComponentTemplateWelcomeDismissed,
  (source, welcomeDismissed) => {
    if (welcomeDismissed) return undefined;
    return source
      ? getUneditedCodeComponentTemplate({code: source.code, ...source.meta})
      : undefined;
  }
);

/**
 * True when an undo just reverted the canvas code component back to its
 * untouched initial source. The DesignerReducer feeds this into
 * codeComponentUIReducer so the creator splash reopens on that transition —
 * checking the post-undo source alone can't distinguish it from the
 * generation window, where the source is still initial but the creator must
 * stay dismissed.
 */
export const didUndoRevertCanvasSourceToPristine = (
  prevState: {DesignerStore: DesignerStateType; PageStore: PageState},
  nextDesignerStore: DesignerStateType
): boolean => {
  const nextSource = selectComponentCanvasCodeComponentSource({
    DesignerStore: nextDesignerStore,
    PageStore: prevState.PageStore,
  });
  if (nextSource?.code !== INITIAL_CODE_COMPONENT_SOURCE) {
    return false;
  }
  const prevSource = selectComponentCanvasCodeComponentSource(prevState);
  return prevSource?.code !== INITIAL_CODE_COMPONENT_SOURCE;
};

/**
 * True when either the generate form or the clarification shell occupies the
 * fullscreen ViewportLayer — used to suppress the right sidebar / AIA dock.
 */
export const selectShowCodeComponentFullscreenOverlay = createSelector(
  selectShowCodeComponentCreator,
  selectShowCodeComponentClarification,
  (showCreator, showClarification) => showCreator || showClarification
);

export const selectCodeComponentDiagnostics = (
  state: CodeLibrariesStateSlice
) => state.CodeComponentUIStore?.diagnostics ?? '';

export const selectSandboxJobStatus = (state: CodeLibrariesStateSlice) =>
  state.RemoteSandboxStore?.job?.status ?? 'IDLE';

/**
 * A rebuild the user (or agent) triggered is in flight. Excludes the cold
 * sandbox start-up build -- `jobStatus` goes QUEUED for that one too, before
 * anything has ever run -- by requiring a prior build *attempt*. Keyed on
 * `buildVersion`, not `evaluatedVersion`: the latter only bumps on success, so
 * a component whose first build fails to compile would never count as past
 * its cold start, hiding the busy state on every rebuild after the fix.
 */
export const selectIsSandboxRebuilding = createSelector(
  selectSandboxJobStatus,
  selectCodeComponentBuildVersion,
  (jobStatus, buildVersion) => jobStatus !== 'IDLE' && buildVersion > 0
);

export const selectRemoteSandbox = createSelector(
  (state: CodeLibrariesStateSlice) => state.RemoteSandboxStore,
  (state: CodeLibrariesStateSlice) => state.SiteDataStore.shortName,
  (state: CodeLibrariesStateSlice) =>
    state.AIAssistantStore.pendingExternalToolCall,
  (sandboxState, siteShortName, pendingExternalToolCall) => ({
    sandboxState,
    siteShortName,
    // We need to ensure a sandbox is created as a side-effect of tool calls
    // that depend on it which is why it's watched by this engine but unused.
    _hasPendingSandboxTool: Boolean(
      pendingExternalToolCall?.tool?.name &&
      SANDBOX_REQUIRED_TOOL_IDS.has(pendingExternalToolCall.tool.name)
    ),
  })
);

const FEDERATION_LOADER_PATH = [{in: 'Record' as const, at: 'loader'}];
const MODULE_ID_PATH = [{in: 'Record' as const, at: 'moduleId'}];
const CLIENT_MODULE_URL_PATH = [{in: 'Record' as const, at: 'clientModuleUrl'}];

function codeModuleIdFromIsland(element: ExpressionElement): string | null {
  const loaderExpr = Expressions.getIn(
    Expressions.getElementData(element),
    FEDERATION_LOADER_PATH
  );
  if (!loaderExpr) {
    return null;
  }
  // `_getIn` on a Union ignores the tag and always walks `union.value`, so a
  // SITE loader would otherwise look like it had a moduleId.
  const union = Expressions.getUnion(loaderExpr);
  if (!union || union.tag !== 'FEDERATION') {
    return null;
  }
  // Authorization resolves the whole batch against stored modules and rejects
  // all of it if any id is unknown, so a locally served module must not ride
  // along and deny the site's real modules their sandbox.
  const bundleUrlExpr = Expressions.getIn(union.value, CLIENT_MODULE_URL_PATH);
  const bundleUrl = bundleUrlExpr ? Expressions.getText(bundleUrlExpr) : null;
  if (bundleUrl && isLoopbackBundleUrl(bundleUrl)) {
    return null;
  }

  const moduleIdExpr = Expressions.getIn(union.value, MODULE_ID_PATH);
  const moduleId = moduleIdExpr ? Expressions.getText(moduleIdExpr) : null;
  if (!moduleId) {
    return null;
  }
  return parseCodeModuleId(moduleId as CodeModuleId) || null;
}

// Code components always have CodeIsland as the render root (compiler and site create).
const collectFromComponent = weakMemo(
  (component: Component.ComponentType): string | null => {
    const element = Expressions.getElement(Component.getRender(component));
    if (!element || !isCodeIsland(element)) {
      return null;
    }
    return codeModuleIdFromIsland(element);
  }
);

/**
 * Sorted, deduped CodeModule ids from CodeIsland FEDERATION loaders in the
 * live component map. SITE loaders are skipped — they carry no module id.
 * The map is site Designer state (staged or persisted), not the current
 * page and not installations: unused and other-page islands still need
 * the sandbox.
 *
 * Modules whose bundle is served from this machine are skipped; see
 * `codeModuleIdFromIsland`.
 */
function collectCodeModuleIds(componentMap: Component.ComponentMap): string[] {
  const ids = new Set<string>();
  componentMap.forEach((component) => {
    const id = collectFromComponent(component);
    if (id) {
      ids.add(id);
    }
  });
  return [...ids].sort();
}

const selectCodeModuleIds = createSelector(selectComponents, (componentMap) =>
  collectCodeModuleIds(componentMap)
);

const selectFunctionsModuleIds = createSelector(
  selectCodeModuleIds,
  selectSiteId,
  (state: CodeLibrariesStateSlice) =>
    state.FunctionsSandboxStore.siteFunctionsHash,
  (codeModuleIds, siteId, hash) =>
    hash && siteId ? [...codeModuleIds, siteId] : codeModuleIds
);

export const selectFunctionsSandboxEngineState = createSelector(
  (state: CodeLibrariesStateSlice) => state.FunctionsSandboxStore,
  (state: CodeLibrariesStateSlice) => state.SiteDataStore.shortName,
  selectFunctionsModuleIds,
  (sandboxState, siteShortName, moduleIds) => ({
    sandboxState,
    siteShortName,
    moduleIds,
  })
);

/**
 * The store's preview while its token is still good. Hands back the store's own
 * object rather than a copy, so selectors built on it stay memoized.
 *
 * FunctionsSandboxStore only carries a preview into CONNECTING/FAILED when the
 * module key matches, so a stale hash's credentials never reach here.
 */
const getUsableFunctionsPreview = (state: CodeLibrariesStateSlice) => {
  const preview = getFunctionsPreview(state.FunctionsSandboxStore);
  if (!preview) return null;
  if (preview.tokenExpiresAt <= Date.now()) return null;
  return preview;
};

const getFunctionsSandboxState = (state: CodeLibrariesStateSlice) =>
  state.FunctionsSandboxStore;

export const selectFunctionsPreviewCredentials = createSelector(
  getUsableFunctionsPreview,
  (preview): FunctionsPreviewCredentials | null =>
    preview ? {origin: preview.origin, token: preview.token} : null
);

/**
 * The readiness mapping keys on this rather than on the status label, so a
 * scheduled refresh or retry is never reported as terminal.
 */
function willEngineProduceCredentials(state: FunctionsSandboxState): boolean {
  switch (state.status) {
    // IDLE is also where the engine parks before the site short name lands,
    // which is a Designer boot window rather than a dead end.
    case 'IDLE':
    case 'CONNECTING':
    case 'AWAITING_REBUILD': // Its queued rebuild moves the store to IDLE.
      return true;
    // Only reached with no usable credentials, i.e. the token expired before
    // the refresh timer ran. The server sets refreshAt at least
    // FUNCTIONS_PREVIEW_REFRESH_MARGIN_MS before tokenExpiresAt, so the
    // reconnect is already overdue rather than far off.
    case 'READY':
      return true;
    // Wait through any scheduled retry, first connect or refresh alike.
    // Callers now await host-owned terminal state directly instead of racing
    // a package-side deadline, so a first connect with a retry pending is no
    // more terminal than a refresh with one.
    case 'FAILED':
      return state.retryAt != null;
    case 'NO_FUNCTIONS':
    case 'DISABLED':
      return false;
  }
}

// Readiness is a separate memoized selector so the config keeps its identity
// across a token refresh: it returns a primitive, so a new sandbox state
// object alone cannot churn it. Fusing it into the config's result function
// makes every refresh a canvas-wide context update.
const selectFunctionsReadiness = createSelector(
  getUsableFunctionsPreview,
  getFunctionsSandboxState,
  (preview, sandboxState): FunctionsReadiness =>
    preview == null && willEngineProduceCredentials(sandboxState)
      ? 'pending'
      : 'settled'
);

const selectSiteFunctionsPreviewModuleId = createSelector(
  getFunctionsSandboxState,
  selectSiteId,
  (sandboxState, siteId) => {
    const hash = sandboxState.siteFunctionsHash;
    return hash &&
      siteId &&
      'moduleKey' in sandboxState &&
      sandboxState.moduleKey.endsWith(`:site-hash=${hash}`)
      ? siteId
      : null;
  }
);

export const selectFunctionsPreviewConfig = createSelector(
  selectFunctionsPreviewCredentials,
  selectFunctionsReadiness,
  selectSiteFunctionsPreviewModuleId,
  (credentials, readiness, siteModuleId): FunctionsPreviewConfig => ({
    credentials,
    readiness,
    siteModuleId,
  })
);
